// 100% client-side sanitization + restoration engine.
// No network calls: all parsing, scrubbing and restoring happen in the browser.

const GENERIC_USERS = new Set([
  "admin", "administrator", "root", "cisco", "user", "guest", "operator",
  "support", "test", "default", "manager", "enable", "system", "sysadmin",
]);

const SECRET_VALUE_BLOCK = new Set([
  "encryption", "required", "min-length", "aes", "des", "none", "max", "disable",
  "enable", "logging", "level", "cleartext", "hash", "sha256", "sha512", "md5",
]);

export const CATEGORY_META = {
  password: { label: "Passwords / Secrets / Keys", color: "#FCA5A5" },
  username: { label: "Usernames", color: "#93C5FD" },
  publicIp: { label: "Public IP Addresses", color: "#86EFAC" },
  email: { label: "Email Addresses", color: "#FCD34D" },
  mac: { label: "MAC Addresses", color: "#C4B5FD" },
  custom: { label: "Custom Keywords", color: "#F9A8D4" },
};

export function defaultOptions() {
  return {
    password: { enabled: true, action: "replace" },
    username: { enabled: true, action: "replace" },
    publicIp: { enabled: true, action: "replace" },
    email: { enabled: true, action: "replace" },
    mac: { enabled: true, action: "replace" },
    custom: { enabled: true, action: "replace", keywords: [] },
  };
}

// Shared context lets a whole batch use one consistent token space so the
// restore key file maps every placeholder back to exactly one original value.
export function createScrubContext() {
  return {
    ipMap: new Map(), ipCount: 0,
    userMap: new Map(), userCount: 0,
    emailMap: new Map(), emailCount: 0,
    macMap: new Map(), macCount: 0,
    secretMap: new Map(), secretCount: 0,
    termMap: new Map(), termCount: 0,
    mapping: new Map(), // replacement token -> { original, category }
  };
}

function register(ctx, token, original, category) {
  if (token) ctx.mapping.set(token, { original, category });
  return token;
}

function octetsValid(ip) {
  const parts = ip.split(".");
  if (parts.length !== 4) return false;
  return parts.every((o) => {
    if (o.length === 0 || o.length > 3) return false;
    const n = Number(o);
    return n >= 0 && n <= 255;
  });
}

function isPublicIp(ip) {
  if (!octetsValid(ip)) return false;
  const [a, b] = ip.split(".").map(Number);
  if (a === 0 || a === 10 || a === 127) return false;
  if (a === 169 && b === 254) return false;
  if (a === 172 && b >= 16 && b <= 31) return false;
  if (a === 192 && b === 168) return false;
  if (a === 100 && b >= 64 && b <= 127) return false;
  if (a >= 224) return false;
  return true;
}

function esc(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildDetectors(options, ctx) {
  const dets = [];

  const apipa = () => {
    const n = ctx.ipCount++;
    const third = 1 + Math.floor(n / 254);
    const fourth = 1 + (n % 254);
    return `169.254.${third}.${fourth}`;
  };

  if (options.password?.enabled) {
    const remove = options.password.action === "remove";
    const replaceSecret = (v) => {
      if (remove) return "";
      if (!ctx.secretMap.has(v)) ctx.secretMap.set(v, `\u27EASECRET_${++ctx.secretCount}\u27EB`);
      return register(ctx, ctx.secretMap.get(v), v, "password");
    };
    dets.push({
      category: "password",
      regex: /<(password|passwd|pwd|secret|token|apikey|api_key|community|psk|passphrase)>([^<]+)<\/\1>/gid,
      group: 2,
      skip: (v) => SECRET_VALUE_BLOCK.has(v.toLowerCase()) || v.trim().length < 3,
      replace: replaceSecret,
    });
    dets.push({
      category: "password",
      regex: /\b(?:password|passwd|pwd|secret|secret[_-]?key|api[_-]?key|apikey|access[_-]?key|private[_-]?key|token|auth[_-]?token|psk|pre-?shared-?key|passphrase|community|credential)\b[\s:="']*(?:\d+\s+)?(["']?)([^"'\s,;<>]{3,})\1/gid,
      group: 2,
      skip: (v) => SECRET_VALUE_BLOCK.has(v.toLowerCase()) || v.trim().length < 3,
      replace: replaceSecret,
    });
  }

  if (options.username?.enabled) {
    const remove = options.username.action === "remove";
    dets.push({
      category: "username",
      regex: /\b(?:username|user-id|userid|user-name)\b[\s:="']*(["']?)([^"'\s,;<>]+)\1/gid,
      group: 2,
      skip: (v) => GENERIC_USERS.has(v.toLowerCase()),
      replace: (v) => {
        if (remove) return "";
        if (!ctx.userMap.has(v)) ctx.userMap.set(v, `USER_${++ctx.userCount}`);
        return register(ctx, ctx.userMap.get(v), v, "username");
      },
    });
  }

  if (options.email?.enabled) {
    const remove = options.email.action === "remove";
    dets.push({
      category: "email",
      regex: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/gid,
      group: 0,
      replace: (v) => {
        if (remove) return "";
        if (!ctx.emailMap.has(v)) ctx.emailMap.set(v, `user${++ctx.emailCount}@example.com`);
        return register(ctx, ctx.emailMap.get(v), v, "email");
      },
    });
  }

  if (options.mac?.enabled) {
    const remove = options.mac.action === "remove";
    const macColon = (v) => {
      if (remove) return "";
      if (!ctx.macMap.has(v)) {
        const n = (++ctx.macCount).toString(16).padStart(2, "0").slice(-2);
        ctx.macMap.set(v, `02:00:00:00:00:${n}`);
      }
      return register(ctx, ctx.macMap.get(v), v, "mac");
    };
    const macCisco = (v) => {
      if (remove) return "";
      if (!ctx.macMap.has(v)) {
        const n = (++ctx.macCount).toString(16).padStart(4, "0").slice(-4);
        ctx.macMap.set(v, `0200.0000.${n}`);
      }
      return register(ctx, ctx.macMap.get(v), v, "mac");
    };
    dets.push({ category: "mac", regex: /\b(?:[0-9A-Fa-f]{4}\.){2}[0-9A-Fa-f]{4}\b/gid, group: 0, replace: macCisco });
    dets.push({ category: "mac", regex: /\b(?:[0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}\b/gid, group: 0, replace: macColon });
  }

  if (options.publicIp?.enabled) {
    const remove = options.publicIp.action === "remove";
    dets.push({
      category: "publicIp",
      regex: /\b(?:\d{1,3}\.){3}\d{1,3}\b/gid,
      group: 0,
      skip: (v) => !isPublicIp(v),
      replace: (v) => {
        if (remove) return "";
        if (!ctx.ipMap.has(v)) ctx.ipMap.set(v, apipa());
        return register(ctx, ctx.ipMap.get(v), v, "publicIp");
      },
    });
  }

  if (options.custom?.enabled) {
    const kws = (options.custom.keywords || []).map((k) => k.trim()).filter(Boolean);
    if (kws.length) {
      const remove = options.custom.action === "remove";
      const pattern = kws.map(escRe).join("|");
      dets.push({
        category: "custom",
        regex: new RegExp(`(${pattern})`, "gid"),
        group: 1,
        replace: (v) => {
          if (remove) return "";
          if (!ctx.termMap.has(v)) ctx.termMap.set(v, `\u27EATERM_${++ctx.termCount}\u27EB`);
          return register(ctx, ctx.termMap.get(v), v, "custom");
        },
      });
    }
  }

  return dets;
}

function collectMatches(text, options, ctx) {
  const dets = buildDetectors(options, ctx);
  const all = [];
  dets.forEach((det, pri) => {
    const re = det.regex;
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m[0] === "") { re.lastIndex++; continue; }
      const idx = m.indices && m.indices[det.group];
      if (!idx) continue;
      const [start, end] = idx;
      const value = text.slice(start, end);
      if (det.skip && det.skip(value)) continue;
      const replacement = det.replace(value);
      all.push({ start, end, pri, category: det.category, value, replacement });
    }
  });
  all.sort((x, y) => x.start - y.start || x.pri - y.pri || y.end - x.end);
  const res = [];
  let lastEnd = 0;
  for (const mt of all) {
    if (mt.start >= lastEnd) { res.push(mt); lastEnd = mt.end; }
  }
  return res;
}

export function scrub(text, options, ctx) {
  ctx = ctx || createScrubContext();
  const matches = collectMatches(text, options, ctx);
  let scrubbed = "";
  let origHtml = "";
  let scrubHtml = "";
  let last = 0;
  const changes = [];
  const summary = {};

  for (const mt of matches) {
    const gap = text.slice(last, mt.start);
    scrubbed += gap;
    origHtml += esc(gap);
    scrubHtml += esc(gap);

    scrubbed += mt.replacement;
    origHtml += `<mark class="rm">${esc(mt.value)}</mark>`;
    if (mt.replacement) scrubHtml += `<mark class="ad">${esc(mt.replacement)}</mark>`;

    changes.push({ category: mt.category, original: mt.value, replacement: mt.replacement });
    summary[mt.category] = (summary[mt.category] || 0) + 1;
    last = mt.end;
  }
  const tail = text.slice(last);
  scrubbed += tail;
  origHtml += esc(tail);
  scrubHtml += esc(tail);

  return {
    scrubbed,
    origLines: origHtml.split("\n"),
    scrubLines: scrubHtml.split("\n"),
    changes,
    summary,
    total: changes.length,
  };
}

// Reverse a sanitized (and possibly contractor-edited) file using a mapping.
// Position-independent: finds each placeholder token wherever it now sits.
export function restoreText(text, mappingArray) {
  const entries = [...mappingArray].sort((a, b) => b.token.length - a.token.length);
  let out = text;
  const report = [];
  for (const { token, original, category } of entries) {
    const found = out.split(token).length - 1;
    if (found > 0) out = out.split(token).join(original);
    report.push({ token, original, category, found });
  }
  return { output: out, report };
}

export const ACCEPTED_EXT = [".txt", ".cfg", ".xml", ".json", ".conf"];

export function isAcceptedFile(name) {
  const lower = name.toLowerCase();
  return ACCEPTED_EXT.some((e) => lower.endsWith(e));
}

export function sanitizedFilename(name) {
  const dot = name.lastIndexOf(".");
  if (dot === -1) return `${name}_sanitized`;
  return `${name.slice(0, dot)}_sanitized${name.slice(dot)}`;
}

export function restoredFilename(name) {
  const cleaned = name.replace(/_sanitized(?=\.[^.]+$|$)/, "");
  const dot = cleaned.lastIndexOf(".");
  if (dot === -1) return `${cleaned}_restored`;
  return `${cleaned.slice(0, dot)}_restored${cleaned.slice(dot)}`;
}
